import clsx from "clsx";
import React, { useEffect, useState } from "react";

import { ChatType } from "@api/graphql/types-and-hooks";
import { ChatDetailHeader } from "@pages/FronteraClarity/components/ChatDetailHeader";
import { ChatDetailSidebar } from "@pages/FronteraClarity/components/ChatDetailSidebar";
import { LiveChat } from "@pages/FronteraClarity/components/LiveChat";
import { VideoSessionDetails, VideoSessionDetailsPinnedNote } from "@pages/VideoSessions/VideoSessionDetails";
import { useBreakpoint } from "@pages/VideoSessions/hooks/useMediaMatcher";
import { VideoSessionMode, VideoSessionProvider, VideoSessionTab } from "@providers/VideoSessionProvider";

interface ExpertChatDetailProps {
  chat: ChatType;
  setSelectedChat?: React.Dispatch<React.SetStateAction<ChatType | undefined>>;
}

export const ExpertChatDetail: React.FC<ExpertChatDetailProps> = ({ chat, setSelectedChat }) => {
  const { isMobile, isDesktop } = useBreakpoint();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  // When set, mounts the full VideoSessionDetailsV2 overlay inline (no
  // route navigation) — same UI used at /video-sessions-v2/:mediaId, just
  // with the activity tab only + the source chat message as a pinned
  // note above the comment list.
  const [overlayState, setOverlayState] = useState<{
    mediaId: string;
    pinnedNote?: VideoSessionDetailsPinnedNote;
    initialSeekTime?: number;
    highlightCommentId?: string;
  } | null>(null);

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  const handleVideoClick = (params: {
    mediaId: string;
    pinnedNote?: VideoSessionDetailsPinnedNote;
    initialSeekTime?: number;
    highlightCommentId?: string;
  }) => {
    setOverlayState(params);
  };

  return (
    <div className={clsx("flex flex-col h-full bg-limestone-50", !isDesktop && "border-l rounded-md")}>
      <div className={clsx("flex h-full overflow-hidden", !isMobile && "gap-2")}>
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden min-w-0">
          <div className="shrink-0">
            <ChatDetailHeader
              chat={chat}
              sidebarOpen={sidebarOpen}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
              setSelectedChat={setSelectedChat}
            />
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            <LiveChat chat={chat} onVideoClick={handleVideoClick} />
          </div>
        </div>

        <ChatDetailSidebar chat={chat} isOpen={sidebarOpen} setOpen={setSidebarOpen} onMediaClick={handleVideoClick} />
      </div>

      {overlayState && (
        // key forces a clean remount when the user opens a different
        // chat video while the overlay is already mounted — this lets
        // VideoSessionDetails seed activeCommentId from
        // initialHighlightCommentId via useState directly.
        <VideoSessionProvider
          key={overlayState.mediaId}
          mode={VideoSessionMode.Overlay}
          mediaIdOverride={overlayState.mediaId}
        >
          <VideoSessionDetails
            mode={VideoSessionMode.Overlay}
            availableTabs={[VideoSessionTab.Activity]}
            pinnedNote={overlayState.pinnedNote}
            initialSeekTime={overlayState.initialSeekTime}
            initialHighlightCommentId={overlayState.highlightCommentId}
            headerOverride={{
              learnerFullName: chat.learner?.learnermetadata
                ? `${chat.learner.learnermetadata.firstName} ${chat.learner.learnermetadata.lastName}`
                : "",
              caption: "Caregiver video",
            }}
            onClose={() => setOverlayState(null)}
          />
        </VideoSessionProvider>
      )}
    </div>
  );
};
